import { authWSClient } from 'WSClient';
import { upperFirst, get } from 'Utils/common';
import * as notificationApi from 'Api/notification';
import {
  SET_ALL,
  SET_NOTIFICATION_LOADING,
  ADD_NOTIFICATIONS_FROM_WS,
  SET_NOTIFICATION_AS_READ,
  ADD_NOTIFICATIONS_FROM_API,
  REMOVE_NOTIFICATION,
  LESS_NOTIFICATIONS,
} from './mutation-types';
import initialState from './state';

function getChannelRealName(channel) {
  return channel === 'updates' ? 'general-updates' : 'general-system';
}

export default {
  resetNotifications({ commit }) {
    commit(SET_ALL, initialState());
  },
  setupNotification({ dispatch }) {
    authWSClient.subscribe(
      'general-system',
      { numMessagesFromArchive: 0 },
      message => dispatch('handleNotification', message),
    );
    authWSClient.subscribe(
      'general-updates',
      { numMessagesFromArchive: 0 },
      message => dispatch('handleNotification', message),
    );
  },
  handleNotification({ commit, getters, dispatch }, message) {
    // If there is command in message, means it's not message, but command
    // pass to handler in action and exit
    if (get(message, 'message.command')) {
      dispatch('handleNotificationAsCommand', message);
      return;
    }
    const channel = message.channel === 'general-updates' ? 'Updates' : 'System';
    const channelData = getters[`notification${channel}Data`];
    // Do not add duplications
    if (
      channelData.some(m => m.id === message.id)
    ) return;
    // update notification counts
    dispatch('getNotificationCounts');
    commit(ADD_NOTIFICATIONS_FROM_WS, { message, channel });
  },
  // handler notification in channel as command
  handleNotificationAsCommand({ dispatch }, message) {
    const command = get(message, 'message.command'); // get command
    const channel = message.channel.replace(/.+-/, ''); // normalize channel name
    switch (command) {
      case 'mark_all_read': { // set mark as read all notification in channel
        dispatch('setAllNotificationsAsReadLocal', channel);
        break;
      }
      case 'mark_read_on_meta': { // special mark as read command for update docs and etc
        const data = get(message, 'message.data');
        // Return if not data
        if (!data) return;
        dispatch('getNotificationCounts', true); // get new notification number
        break;
      }
      default:
        break;
    }
  },
  setNotificationAsRead({ commit }, id) {
    authWSClient.setMessageAsRead(id);
    commit(SET_NOTIFICATION_AS_READ, { id });
  },
  // Set all notifications as read in current store state
  setAllNotificationsAsReadLocal({ dispatch }) {
    setTimeout(() => {
      dispatch('getNotificationCounts', true);
    });
  },
  setAllNotificationsAsRead({ dispatch }, channel) {
    if (channel) {
      const realChannelName = getChannelRealName(channel);
      authWSClient.setAllMessagesAsRead({ channel: realChannelName });
    } else {
      authWSClient.setAllMessagesAsRead();
    }
    dispatch('setAllNotificationsAsReadLocal', channel);
  },
  setNotificationsAsReadByDocumentId({ dispatch }, { code, documentId }) {
    authWSClient.send('mark_read_on_meta', {
      document_id: String(documentId),
      code,
    });
    setTimeout(() => dispatch('getNotificationCounts', true));
  },
  /**
   *
   * @param { commit } - context
   * @param isUpdateNotifications - is reset current notification lists by getting of this action
   * @returns {Promise<Promise<[Promise, Promise]>|*>}
   */
  async getNotificationCounts({ commit, state }, isUpdateNotifications) {
    try {
      if (state.notificationsCountsLoading) {
        return Promise.all([
          state.getNotificationsSystemPromise,
          state.getNotificationsUpdatesPromise,
        ]); // Prevent doubling fetch
      }
      commit(SET_ALL, { notificationsCountsLoading: true });
      state.getNotificationsSystemPromise = notificationApi
        .getNotifications('general-system', 20);
      state.getNotificationsUpdatesPromise = notificationApi
        .getNotifications('general-updates', 20);
      const { data: systemData } = await state.getNotificationsSystemPromise;
      const { data: updatesData } = await state.getNotificationsUpdatesPromise;
      commit(SET_ALL, { notificationsCountsLoading: false });
      commit(SET_ALL, {
        notificationSystemCount: Number(systemData.count) || 0,
        notificationSystemNew: Number(systemData.new) || 0,
        notificationUpdatesCount: Number(updatesData.count) || 0,
        notificationUpdatesNew: Number(updatesData.new) || 0,
      });
      if (isUpdateNotifications) {
        commit(SET_ALL, {
          notificationSystemData: systemData.data,
          notificationUpdatesData: updatesData.data,
        });
      }
      return Promise.all([
        state.getNotificationsSystemPromise,
        state.getNotificationsUpdatesPromise,
      ]);
    } catch (error) {
      return error;
    }
  },
  moreNotifications({ commit, getters }, { channel, notificationNumber = 10 } = {}) {
    const capitalizedChannel = upperFirst(channel);
    const realChannelName = getChannelRealName(channel);
    const loadedItems = getters[`notification${capitalizedChannel}Data`];
    commit(SET_NOTIFICATION_LOADING, { channel: capitalizedChannel });
    return notificationApi.getNotifications(realChannelName, notificationNumber, loadedItems.length)
      .then(({ data }) => {
        commit(ADD_NOTIFICATIONS_FROM_API, { data: data.data, channel: capitalizedChannel });
        return data;
      })
      .catch((error) => {
        commit(SET_NOTIFICATION_LOADING, { channel: capitalizedChannel, loading: false });
        return error;
      });
  },
  async replaceJustReadNotificationByUnread({ dispatch, commit }, { channel, itemId }) {
    dispatch('setNotificationAsRead', itemId);
    commit(REMOVE_NOTIFICATION, { channel, itemId });
    dispatch('moreNotifications', { channel, notificationNumber: 1 });
  },
  lessNotifications({ commit }, channel) {
    const capitalizedChannel = upperFirst(channel);
    commit(LESS_NOTIFICATIONS, { channel: capitalizedChannel });
  },
};
